/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import {
  UpdateClinicProfileSchema,
  type UpdateClinicProfileInput,
} from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { type FieldErrors, type UseFormRegister, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { type ClinicProfile } from '../../lib/api/clinic.js';
import { useUpdateClinicProfile } from '../../lib/queries/clinic.js';

interface SettingsFormProps {
  readonly profile: ClinicProfile;
}

function toDefaults(profile: ClinicProfile): UpdateClinicProfileInput {
  return {
    name: profile.name,
    contactEmail: profile.contactEmail,
    contactPhone: profile.contactPhone,
    timezone: profile.timezone,
    ...(profile.legalName !== null ? { legalName: profile.legalName } : {}),
    ...(profile.gstin !== null ? { gstin: profile.gstin } : {}),
    ...(profile.logoUrl !== null ? { logoUrl: profile.logoUrl } : {}),
    address: {
      line1: profile.address.line1,
      city: profile.address.city,
      state: profile.address.state,
      postalCode: profile.address.postalCode,
      country: profile.address.country,
      ...(profile.address.line2 !== undefined ? { line2: profile.address.line2 } : {}),
    },
  };
}

interface AddressFieldsProps {
  readonly register: UseFormRegister<UpdateClinicProfileInput>;
  readonly errors: FieldErrors<UpdateClinicProfileInput>;
}

const ADDRESS_FIELDS = [
  { key: 'line1', label: 'settings.line1' },
  { key: 'line2', label: 'settings.line2' },
  { key: 'city', label: 'settings.city' },
  { key: 'state', label: 'settings.state' },
  { key: 'postalCode', label: 'settings.postalCode' },
  { key: 'country', label: 'settings.country' },
] as const;

function AddressFields({ register, errors }: AddressFieldsProps): JSX.Element {
  const { t } = useTranslation();
  const addr = errors.address;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {ADDRESS_FIELDS.map((field) => (
        <FormField
          key={field.key}
          id={`addr-${field.key}`}
          type="text"
          label={t(field.label)}
          autoComplete="off"
          error={addr?.[field.key]?.message}
          registration={register(`address.${field.key}`)}
        />
      ))}
    </div>
  );
}

const MAIN_FIELDS = [
  { key: 'name', label: 'settings.name', type: 'text' },
  { key: 'legalName', label: 'settings.legalName', type: 'text' },
  { key: 'contactEmail', label: 'settings.email', type: 'email' },
  { key: 'contactPhone', label: 'settings.phone', type: 'tel' },
  { key: 'timezone', label: 'settings.timezone', type: 'text' },
  { key: 'gstin', label: 'settings.gstin', type: 'text' },
  { key: 'logoUrl', label: 'settings.logoUrl', type: 'url' },
] as const;

function MainFields({ register, errors }: AddressFieldsProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {MAIN_FIELDS.map((f) => (
        <FormField
          key={f.key}
          id={`clinic-${f.key}`}
          type={f.type}
          label={t(f.label)}
          autoComplete="off"
          error={errors[f.key]?.message}
          registration={register(f.key)}
        />
      ))}
    </div>
  );
}

/** Clinic Admin → Settings form (Figma). Validated by `UpdateClinicProfileSchema`. */
export function SettingsForm({ profile }: SettingsFormProps): JSX.Element {
  const { t } = useTranslation();
  const update = useUpdateClinicProfile();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<UpdateClinicProfileInput>({
    resolver: zodResolver(UpdateClinicProfileSchema),
    defaultValues: toDefaults(profile),
  });

  const submit = useCallback(
    async (values: UpdateClinicProfileInput): Promise<void> => {
      await update.mutateAsync(values);
    },
    [update],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="max-w-3xl space-y-5" noValidate>
      <MainFields register={register} errors={errors} />

      <h2 className="pt-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
        {t('settings.address')}
      </h2>
      <AddressFields register={register} errors={errors} />

      {update.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}
      {update.isSuccess && (
        <p role="status" className="text-sm text-emerald-600">
          {t('settings.saved')}
        </p>
      )}

      <Button type="submit" disabled={isSubmitting || update.isPending}>
        {t('settings.save')}
      </Button>
    </form>
  );
}
